import { useMemo, useState } from 'react';
import { CheckCircle2, Ticket } from 'lucide-react';
import { api } from '@/core/api/queries';
import { getFieldErrors } from '@/core/api/response-handler';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { formatUnixTimestamp } from '@/shared/utils/formatting';
import { ProgresRang } from '../components/ProgresRang';
import { MagazinePartenere } from '../../../shared/components/Parteneri';
import { NivelPremiu } from '../components/NivelPremiu';
import { RedeemSheet } from '../components/RedeemSheet';
import { RedemptionStatusBadge } from '../components/RedemptionStatusBadge';
import { useTicketMutations } from '../hooks/use-ticket-mutations';
import { subsolPremii } from '../utils/descriere-premii';
import { calculeazaRang } from '../utils/rang';
import { computeTierState } from '../utils/tier-state';
import type { TicketTierDto, TicketRedemptionDto } from '../api/tickets.api';

/**
 * „Puncte și premii" - pagina coordonatorului (spec §8, item 15 din 2026-09-21).
 *
 * De sus in jos: soldul, apoi O SINGURA lista de niveluri — pozitia scolii, optiunile de premiu,
 * costul si „Revendică" pe acelasi rand —, apoi istoricul. Pana la item 15 nivelurile apareau de doua
 * ori: o scara (pozitia) si o grila de carduri (premiul + butonul).
 *
 * Mobile-first, o coloana; un singur CTA per rand; confirmarea de DUPA e ecran, nu toast (R12), iar
 * cea de DINAINTE e bottom sheet, pentru ca e o decizie.
 */
export default function SchoolPointsPage() {
    const balance = api.tickets.ownBalance.useQuery();
    const tiers = api.tickets.tiers.useQuery({ variables: {} });
    const redemptions = api.tickets.ownRedemptions.useQuery();
    const { redeem } = useTicketMutations();

    const [pending, setPending] = useState<TicketTierDto | null>(null);
    const [done, setDone] = useState<TicketRedemptionDto | null>(null);
    const [eroareAlegere, setEroareAlegere] = useState<string | undefined>(undefined);

    const available = balance.data?.available ?? 0;
    const own = useMemo(() => redemptions.data ?? [], [redemptions.data]);
    const niveluri = useMemo(() => tiers.data ?? [], [tiers.data]);

    /**
     * Totalul castigat in editie. Decide NIVELUL, si nu se atinge la revendicare.
     *
     * 🔴 Nu se inlocuieste cu soldul: tinta barei calculata din sold facea bara sa scada la fiecare
     * revendicare — ecranul spunea ca scoala a fost retrogradata fiindca a cheltuit.
     */
    const earned = balance.data?.earned ?? 0;

    /** Pozitia scolii, o singura data: o citesc si capul paginii, si fiecare rand din lista. */
    const rang = useMemo(() => calculeazaRang(niveluri, earned), [niveluri, earned]);

    /** Regula grilei, sau `null` cand nu exista niveluri -- decizia sta in modulul testat. */
    const subsol = subsolPremii(niveluri.length);

    if (balance.isLoading || tiers.isLoading || redemptions.isLoading) {
        return <div className="flex justify-center p-8"><Spinner /></div>;
    }

    if (balance.isError || !balance.data) {
        return (
            <p className="p-4 text-destructive">
                Soldul nu a putut fi încărcat. Școala trebuie să fie aprobată în ediția curentă.
            </p>
        );
    }

    // Ecranul de DUPA (R12). Onest ca starea e „Solicitat", nu „Livrat": ce s-a intamplat, in ce
    // stare e, cine urmeaza sa actioneze, si unde se vede mai tarziu.
    if (done) {
        return (
            <div className="mx-auto flex max-w-md flex-col items-center gap-4 p-6 text-center">
                <CheckCircle2 className="size-16 text-magenta" aria-hidden />
                <h1 className="text-fluid-2xl font-bold">Premiu solicitat</h1>
                <Card className="w-full py-0 text-left">
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                        <div className="min-w-0">
                            <p className="font-semibold">{done.tierName}</p>
                            {done.alegereProfesor && (
                                <p className="text-sm break-words">{done.alegereProfesor}</p>
                            )}
                            <p className="text-sm text-muted-foreground tabular-nums">
                                {done.pointsSpent} puncte
                            </p>
                        </div>
                        <RedemptionStatusBadge status={done.status ?? 'REQUESTED'} />
                    </CardContent>
                </Card>
                <p className="text-sm text-muted-foreground tabular-nums">
                    Îți rămân <span className="font-semibold">{available}</span> puncte.
                </p>
                <p className="text-sm text-muted-foreground">
                    Echipa ECOTIC confirmă livrarea. Vei vedea starea aici, în istoric.
                </p>
                <Button
                    className="touch-target min-h-12 w-full bg-magenta hover:bg-magenta-hover"
                    onClick={() => setDone(null)}
                >
                    Înapoi la premii
                </Button>
            </div>
        );
    }

    return (
        /* `w-full` NU e redundant langa `max-w-3xl`. `<main>` e `display:flex`, deci containerul
           asta e un FLEX ITEM si, fara latime declarata, se dimensioneaza dupa continut: masurat,
           435,56px intr-un viewport de 360, adica derulare orizontala pe toata pagina. `max-w-3xl`
           limiteaza doar in sus; nu obliga la nimic in jos.

           SE-19: `min-h-0` + `overflow-y-auto`, aceeasi familie ca RecipienteAdminPage.tsx
           (P1-3/P0-6) -- shell-ul taie overflow-ul NECONDITIONAT, iar cu multe niveluri + istoric
           lung continutul iesea din viewport fara nicio cale de derulare.

           🔴 Scroller-ul si coloana sunt DOUA elemente, nu unul: cu `max-w-3xl` pe scroller, bara
           de derulare cadea la marginea coloanei, nu la marginea zonei de continut. */
        <div className="min-h-0 w-full overflow-y-auto">
            <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4">
                <h1 className="text-fluid-2xl font-bold">Puncte și premii</h1>
                <ProgresRang rang={rang} available={available} />
                <MagazinePartenere />

                <section className="space-y-3">
                    <h2 className="text-fluid-xl font-semibold">Premii</h2>
                    {niveluri.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                            Nivelurile pentru această ediție nu au fost încă publicate.
                        </p>
                    )}
                    {/* Toate nivelurile, inclusiv cele neatinse: fiecare spune ce mai e de facut.
                        Ordinea e a scarii (dupa cost, vezi `rang.ts`), deci „atins" urca monoton. */}
                    {rang.niveluri.length > 0 && (
                        <ul className="m-0 flex list-none flex-col gap-3 p-0">
                            {rang.niveluri.map((nivel) => (
                                <NivelPremiu
                                    key={nivel.tier.id ?? nivel.index}
                                    nivel={nivel}
                                    total={rang.niveluri.length}
                                    earned={earned}
                                    state={computeTierState(nivel.tier, available, own)}
                                    busy={redeem.isPending}
                                    onRevendica={setPending}
                                />
                            ))}
                        </ul>
                    )}
                    {/* Regula jocului, O SINGURA DATA pentru toata lista: e o regula despre niveluri,
                        nu despre un nivel. Nu spune „nu sunt cumulative" -- vezi `descriere-premii.ts`. */}
                    {subsol && <p className="text-xs text-muted-foreground">{subsol}</p>}
                </section>

                <section className="space-y-3">
                    <h2 className="text-fluid-xl font-semibold">Istoric</h2>
                    {own.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Aici apar premiile revendicate de școala ta.
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-2">
                            {own.map((r) => <RedemptionRow key={r.id} redemption={r} />)}
                        </ul>
                    )}
                </section>

                <RedeemSheet
                    tier={pending}
                    available={available}
                    busy={redeem.isPending}
                    eroareAlegere={eroareAlegere}
                    onCancel={() => { setPending(null); setEroareAlegere(undefined); }}
                    onConfirm={async (alegereProfesor) => {
                        if (!pending?.id) return;
                        setEroareAlegere(undefined);
                        try {
                            const rezultat = await redeem.mutateAsync({ tierId: pending.id, alegereProfesor });
                            setPending(null);
                            setDone(rezultat);
                        } catch (err) {
                            // Eroarea generala e deja aratata ca toast de useTicketMutations; daca e
                            // specific pe campul "alegere" (400, text.prea.lung), o arat si langa camp
                            // — sheet-ul ramane deschis, ca sa se vada peste ce s-a intamplat.
                            setEroareAlegere(getFieldErrors(err)?.alegereProfesor);
                        }
                    }}
                />
            </div>
        </div>
    );
}

/**
 * Un rand de istoric. Structura de card mobil: inaltime MINIMA (nu fixa), titlu, alegere, stare, cifre.
 *
 * `alegereProfesor` (ce a ales coordonatorul la revendicare) e DISTINCT de `note` (motivul ECOTIC la
 * anulare): campuri separate pe DTO, randuri separate aici. Alegerea nu se trunchiaza — e exact textul
 * care a plecat in emailuri, si omul trebuie sa-l poata citi intreg.
 *
 * La `CANCELLED`, punctele se arata taiate si estompate - e singurul semnal vizual ca s-au intors
 * in sold, fiindca nu exista registru de tranzactii si nici rand de credit.
 */
function RedemptionRow({ redemption: r }: { redemption: TicketRedemptionDto }) {
    const anulat = r.status === 'CANCELLED';
    const cand = formatUnixTimestamp(r.redeemedDt);

    return (
        <li className="flex min-h-[72px] items-center justify-between gap-3 rounded-lg border p-3">
            <div className="flex min-w-0 items-center gap-3">
                <Ticket className="size-5 shrink-0 text-magenta" aria-hidden />
                <div className="min-w-0">
                    <p className="truncate font-medium">{r.tierName}</p>
                    {r.alegereProfesor && (
                        <p className="mt-0.5 text-sm break-words">
                            <span className="text-muted-foreground">Alegere: </span>{r.alegereProfesor}
                        </p>
                    )}
                    {cand && <p className="text-xs text-muted-foreground">{cand}</p>}
                    {anulat && r.note && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Motiv: {r.note} · punctele au revenit în sold
                        </p>
                    )}
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-3">
                <span className={anulat ? 'text-sm tabular-nums text-muted-foreground line-through' : 'text-sm tabular-nums'}>
                    −{r.pointsSpent}
                </span>
                <RedemptionStatusBadge status={r.status ?? 'REQUESTED'} />
            </div>
        </li>
    );
}
